import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, ChevronRight, Save, X, RefreshCw, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NegativeStockAudit() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [editingTxn, setEditingTxn] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [correcting, setCorrecting] = useState(null);

  const fetchAudit = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const res = await axios.get(`${API}/audit/negative-stock`, {
        
      });
      setData(res.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudit(false); }, [fetchAudit]);

  const toggleRow = (idx) => setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));

  const startEdit = (txn) => {
    setEditingTxn(txn);
    setEditQty(Math.abs(txn.qty_change).toString());
  };

  const cancelEdit = () => {
    setEditingTxn(null);
    setEditQty('');
  };

  const saveEdit = async () => {
    if (!editingTxn) return;
    try {
      await axios.put(
        `${API}/audit/fix-transaction/${editingTxn.collection}/${editingTxn.id}`,
        { qty: parseInt(editQty) },
        {}
      );
      toast.success('Transaction updated. Recalculating...');
      cancelEdit();
      fetchAudit(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update transaction');
    }
  };

  const handleAutoCorrect = async (item) => {
    setCorrecting(`${item.part_id}_${item.stage}`);
    try {
      const res = await axios.post(
        `${API}/audit/auto-correct/${encodeURIComponent(item.part_id)}/${encodeURIComponent(item.stage)}`,
        {},
        {}
      );
      toast.success(res.data.message);
      fetchAudit(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create correction entry');
    } finally {
      setCorrecting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="audit-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="negative-stock-audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="audit-title">
            NEGATIVE STOCK AUDIT
          </h1>
          <p className="text-sm text-text-muted mt-2">Identify and fix transactions causing negative stock balances</p>
        </div>
        <Button onClick={() => fetchAudit(true)} variant="outline" className="rounded-sm" data-testid="refresh-audit-button">
          <RefreshCw className="w-4 h-4 mr-2" />REFRESH
        </Button>
      </div>

      {data.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-sm p-8 text-center" data-testid="no-negatives">
          <div className="text-green-600 text-lg font-semibold">All stock balances are healthy</div>
          <p className="text-sm text-green-500 mt-1">No negative stock detected across any stage</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 flex items-center gap-3" data-testid="negative-count-banner">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-red-800">
              {data.length} negative stock {data.length === 1 ? 'entry' : 'entries'} found — expand to view transaction history and fix inline
            </span>
          </div>

          {data.map((item, idx) => (
            <div key={`${item.part_id}-${item.stage}`} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`audit-item-${idx}`}>
              {/* Header row */}
              <div
                className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleRow(idx)}
                data-testid={`audit-row-${idx}`}
              >
                {expandedRows[idx]
                  ? <ChevronDown className="w-4 h-4 text-text-muted flex-shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-text-muted flex-shrink-0" />
                }
                <div className="flex-1 flex items-center gap-6">
                  <span className="font-mono text-sm font-bold">{item.part_id}</span>
                  {item.part_name && <span className="text-sm text-text-muted">{item.part_name}</span>}
                  <span className="inline-flex items-center rounded-full bg-gray-100 border border-gray-200 px-2.5 py-0.5 text-xs font-semibold">
                    {item.stage}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold font-mono text-red-600" data-testid={`audit-balance-${idx}`}>
                    {item.current_balance} pcs
                  </span>
                </div>
              </div>

              {/* Offending transaction highlight + Auto-correct button */}
              {item.offending_transaction && (
                <div className="mx-4 mb-2 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-sm px-3 py-2 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <span className="text-xs text-red-700">
                      <span className="font-semibold">Root cause:</span> {item.offending_transaction.detail}
                      {item.offending_transaction.date && (
                        <span className="text-red-400 ml-2">
                          ({new Date(item.offending_transaction.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })})
                        </span>
                      )}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleAutoCorrect(item); }}
                    disabled={correcting === `${item.part_id}_${item.stage}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs h-8 px-3"
                    data-testid={`auto-correct-${idx}`}
                  >
                    <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
                    {correcting === `${item.part_id}_${item.stage}` ? 'CREATING...' : `CREATE +${Math.abs(item.current_balance)} PCS CORRECTION`}
                  </Button>
                </div>
              )}

              {/* Expanded transaction ledger */}
              {expandedRows[idx] && (
                <div className="border-t border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 w-40">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 w-48">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Detail</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 w-28">Change</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 w-28">Balance</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-600 w-28">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.transactions.map((txn, tidx) => {
                        const isEditing = editingTxn?.id === txn.id && editingTxn?.collection === txn.collection;
                        return (
                          <tr
                            key={`${txn.id}-${tidx}`}
                            className={`border-b last:border-0 ${txn.caused_negative ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                            data-testid={`audit-txn-${idx}-${tidx}`}
                          >
                            <td className="px-4 py-2.5 font-mono text-xs text-text-muted">
                              {txn.date ? new Date(txn.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                txn.type.includes('Issue') || txn.type.includes('Dispatch') || txn.type.includes('Out')
                                  ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                  : 'bg-green-100 text-green-700 border border-green-200'
                              }`}>
                                {txn.type}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-700">
                              {txn.detail}
                              {txn.caused_negative && (
                                <span className="ml-2 text-red-600 font-bold text-[10px] uppercase tracking-wider">caused negative</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={editQty}
                                  onChange={(e) => setEditQty(e.target.value)}
                                  className="w-20 h-7 text-xs text-right inline-block rounded-none"
                                  data-testid={`edit-qty-input-${idx}-${tidx}`}
                                  min="0"
                                  autoFocus
                                />
                              ) : (
                                <span className={txn.qty_change < 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                                  {txn.qty_change > 0 ? '+' : ''}{txn.qty_change}
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-2.5 text-right font-mono text-xs font-semibold ${txn.balance_after < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {txn.balance_after}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={saveEdit} className="p-1 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`save-edit-${idx}-${tidx}`}>
                                    <Save className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={cancelEdit} className="p-1 hover:bg-red-100 text-red-500 rounded-sm" data-testid={`cancel-edit-${idx}-${tidx}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); startEdit(txn); }}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  data-testid={`fix-btn-${idx}-${tidx}`}
                                >
                                  Fix
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
